import { Close } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useRef, useState } from 'react';
import { ArticleModel, ListArticlesResponse } from '../../../../Generated/Raven';
import { DemeterNewsModel, ListDemeterNewsResponse } from '../../../../Generated/Raven-Demeter';
import IconButton from '../../../Components/Form/Buttons/IconButton';
import TextInputWithAutoFocus from '../../../Components/Form/Inputs/TextInputWithAutoFocus';
import ComponentHeader from '../../../Components/Headers/ComponentHeader';
import CardSkeleton from '../../../Components/Layout/CardSkeleton';
import browserService from '../../../Services/BrowserService';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import trackingService, { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import LatestNewsModal from '../../Dashboard/LatestNews/LatestNewsModal';
import styles from './CustomDefaultDashboardComponents.module.scss';

interface ICustomDashboardMarketIntelligenceReportContainerProps {
    title: string;
    isLoading: boolean;
    newsOrArticleResponse: ListArticlesResponse | ListDemeterNewsResponse | undefined;
    clickedArticleIds: string[];
    onSearchTermChange: (value: string) => void;
    onLinkClick: (link: string) => void;
    onSearch: () => void;
    enableModal: boolean;
    useDynamicHeight?: boolean;
    track: TrackingEvent;
}

const articleAuthor = 'author';
const newsAuthor = 'authorName';

const CustomDashboardMarketIntelligenceReportContainer: React.FC<ICustomDashboardMarketIntelligenceReportContainerProps> = (props) => {
    const [translations] = useLanguage();
    const [searchContainerIsOpen, setSearchContainerIsOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [headlineNewsPreviewData, setHeadlineNewsPreviewData] = useState<{ data: DemeterNewsModel; index: number; finalArticleIndex: number }>();
    const searchInputReference = useRef() as React.MutableRefObject<HTMLInputElement>;

    const getAuthorName = (item: DemeterNewsModel | ArticleModel) => {
        if (articleAuthor in item && item?.author?.name) {
            return item.author.name;
        }

        if (newsAuthor in item) {
            return item.authorName;
        }

        return '';
    };

    const handleSearchTermEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const { value } = event.target as HTMLInputElement;
        if (event.key === 'Enter') {
            props.onSearchTermChange(value);
            props.onSearch();
        }
    };

    const handleLinkClick = (item: DemeterNewsModel | ArticleModel, index: number) => {
        const { articleId, languageCode } = item;

        if (props.enableModal && 'isShortForm' in item) {
            const articles = props.newsOrArticleResponse?.rows || [];
            setHeadlineNewsPreviewData({
                data: item as DemeterNewsModel,
                index,
                finalArticleIndex: articles.length - 1,
            });
            setIsModalOpen(true);
        } else {
            browserService.openMarketIntelligenceArticle({
                language: languageCode,
                articleId,
            });
        }

        props.onLinkClick(articleId);

        const rows = (props.newsOrArticleResponse?.rows ?? []) as Array<DemeterNewsModel | ArticleModel>;
        const article = rows.find((x) => x.articleId === articleId);

        if (article) {
            trackingService.track({
                ...props.track,
                eventAction: `${props.track.eventAction}ArticleClicked`,
                eventValue: article.title,
            });
        }
    };

    const handleArticleChange = (isNext: boolean) => {
        const articles = props.newsOrArticleResponse?.rows || [];
        const finalArticleIndex = articles.length - 1;

        if (headlineNewsPreviewData) {
            if (isNext && headlineNewsPreviewData.index < finalArticleIndex) {
                const nextIndex = headlineNewsPreviewData.index + 1;
                setHeadlineNewsPreviewData({
                    data: articles[nextIndex] as DemeterNewsModel,
                    index: nextIndex,
                    finalArticleIndex,
                });
            } else if (!isNext && headlineNewsPreviewData.index > 0) {
                const previousIndex = headlineNewsPreviewData.index - 1;
                setHeadlineNewsPreviewData({
                    data: articles[previousIndex] as DemeterNewsModel,
                    index: previousIndex,
                    finalArticleIndex,
                });
            }
        }
    };

    useEffect(() => {
        props.onSearchTermChange('');
        setSearchInput('');
        if (searchInput.length) {
            props.onSearch();
        }
    }, [searchContainerIsOpen]);

    const contentClassName = props.useDynamicHeight ? styles.market_intelligence_content_dynamic_height : styles.market_intelligence_content;
    const newsItemsClassName = props.useDynamicHeight ? styles.market_intelligence_news_items_dynamic_height : styles.market_intelligence_news_items;

    return (
        <div className={contentClassName}>
            <div className={styles.market_intelligence_header_row}>
                <ComponentHeader title={props.title} useContainerFontSize />
                <div className={styles.market_intelligence_search_container}>
                    {searchContainerIsOpen && (
                        <TextInputWithAutoFocus
                            inputReference={searchInputReference}
                            handleKeyDown={handleSearchTermEnter}
                            className={styles.market_intelligence_search_input}
                            placeholder={translations.actions.search}
                            value={searchInput}
                            handleTextChange={(value) => setSearchInput(value)}
                            track={{ eventCategory: 'Dashboard', eventAction: 'MarketIntelligenceReportSearchInputChanged', eventValue: searchInput }}
                        />
                    )}
                    {!searchContainerIsOpen && (
                        <IconButton
                            handleClick={() => setSearchContainerIsOpen(true)}
                            track={{
                                eventCategory: 'Dashboard',
                                eventAction: 'ShowMarketIntelligenceReportSearchInputClicked',
                            }}
                        >
                            <SearchIcon className={styles.market_intelligence_icon} />
                        </IconButton>
                    )}
                    {searchContainerIsOpen && (
                        <IconButton
                            handleClick={() => setSearchContainerIsOpen(false)}
                            track={{ eventCategory: 'Dashboard', eventAction: 'HideMarketIntelligenceReportSearchInputClicked' }}
                        >
                            <Close className={styles.market_intelligence_icon} />
                        </IconButton>
                    )}
                </div>
            </div>
            {props.isLoading || !props.newsOrArticleResponse ? (
                <CardSkeleton />
            ) : (
                <div className={newsItemsClassName}>
                    {props.newsOrArticleResponse?.rows?.map((x, index) => (
                        <div key={x.articleId} className={styles.market_intelligence_news_item}>
                            {x.heroImageUrl && <img className={styles.market_intelligence_image} src={x.heroImageUrl} alt="#" />}
                            <div>
                                <button
                                    type="button"
                                    onClick={() => handleLinkClick(x, index)}
                                    className={
                                        !props.clickedArticleIds.some((id) => id === x.articleId)
                                            ? styles.market_intelligence_title_new
                                            : styles.market_intelligence_title_visited
                                    }
                                >
                                    {x.title}
                                </button>
                                <div className={styles.market_intelligence_author}>{getAuthorName(x)}</div>
                                <div className={styles.market_intelligence_published_date}>{formattingService.toShortTimeAgoSinceNow(x.publishedDate)}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {headlineNewsPreviewData && props.enableModal && (
                <LatestNewsModal
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    headlineNewsPreviewData={headlineNewsPreviewData}
                    handleArticleChange={handleArticleChange}
                />
            )}
        </div>
    );
};

export default CustomDashboardMarketIntelligenceReportContainer;
